import { Calendar, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const blondeServices = [
  {
    name: 'Balayage',
    description: 'Hand-painted, lived-in blonde for a natural, sun-kissed look. Perfect for low-maintenance colour that grows out beautifully.',
    includes: 'Includes K18 treatment and toner',
  },
  {
    name: 'Full Head Foils',
    description: 'Classic foil highlights for bright, dimensional blonde. Ideal for all-over lightening or maximum brightness.',
    includes: 'Toner included',
  },
  {
    name: 'Teasy Lights',
    description: 'Soft, lived-in blonde with teased sections for a subtle, natural finish. Melbourne\'s most requested blonde technique.',
    includes: 'Includes K18 treatment and toner',
  },
  {
    name: 'Root Melt & Toner',
    description: 'Shadow root with toner refresh to maintain your perfect blonde between full services.',
    includes: 'Perfect maintenance service',
  },
  {
    name: 'All-Over Colour',
    description: 'Rich, full-coverage colour for a complete transformation. Whether you want to go darker, cover greys, or try a bold new shade.',
    includes: 'La Biosthetique premium colour',
  },
  {
    name: 'Colour Correction',
    description: 'Expert correction for previous colour mishaps, box dye removal, or banding. We restore your hair to its healthiest, most beautiful state.',
    includes: 'Consultation required',
  },
];

const blondeTypes = [
  { name: 'Creamy Blonde', description: 'Warm, buttery tones for a soft, natural look' },
  { name: 'Platinum Blonde', description: 'Cool, bright blonde for maximum impact' },
  { name: 'Lived-in Blonde', description: "Low-maintenance colour that grows out beautifully" },
  { name: 'Honey Blonde', description: 'Golden, warm tones perfect for Melbourne\'s climate' },
  { name: 'Ash Blonde', description: 'Cool, smoky blonde without brassiness' },
  { name: 'Babylights', description: 'Ultra-fine highlights for subtle dimension' },
];

export default function BlondeSpecialist() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="blonde-specialist-page">
      <SEO page="blondeSpecialist" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1620717841004-b2129070b674?w=1920&h=800&fit=crop"
            alt="Blonde hair balayage specialist Camberwell"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/20 border border-[#D4C4B0]/50 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A]">Camberwell's Blonde & Colour Experts</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Blondes & Colour Specialist
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Camberwell's destination for perfect blondes and flawless colour. From creamy balayage to rich brunette, fashion colours, and everything in between — our colour specialists focus on hair health and precision results. Located just off Burke Road, near Camberwell Junction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4 text-lg">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Blonde Consultation
                </Button>
              </a>
              <Link to="/hair">
                <Button variant="outline" className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg">
                  View All Hair Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Blonde */}
      <section className="py-16 bg-[#D4C4B0]/20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">Hair Integrity</p>
              <p className="text-[#5D4A3A]">We protect your hair's health</p>
            </div>
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">K18</p>
              <p className="text-[#5D4A3A]">Molecular hair repair</p>
            </div>
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">La Biosthetique</p>
              <p className="text-[#5D4A3A]">Premium colour products</p>
            </div>
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">Lived-in</p>
              <p className="text-[#5D4A3A]">Low-maintenance blonde</p>
            </div>
          </div>
        </div>
      </section>

      {/* Blonde Services */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Our Blonde Services</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Expert Blondes, Foils & Colour</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Whether you want subtle sun-kissed highlights or full platinum transformation, our blonde specialists will create your perfect shade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blondeServices.map((service) => (
              <div key={service.name} className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 hover:border-[#8D7B68]/50 transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-medium text-[#2C2420]">{service.name}</h3>
                </div>
                <p className="text-[#5D4A3A] mb-4">{service.description}</p>
                <p className="text-sm text-[#8D7B68] italic">{service.includes}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Blonde */}
      <section className="py-24 md:py-32 bg-[#D4C4B0]/20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Find Your Perfect Shade</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Blonde for Every Style
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Not all blondes are created equal. Our specialists will help you find the perfect shade to complement your skin tone and lifestyle. Whether you prefer warm, cool, or neutral tones, we'll create a custom blonde just for you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {blondeTypes.map((type) => (
                  <div key={type.name} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#D4C4B0]/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#8D7B68]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2420]">{type.name}</p>
                      <p className="text-sm text-[#5D4A3A]">{type.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-square rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1609369350243-83d11c675ff8?w=600&h=600&fit=crop"
                alt="Beautiful blonde hair colour Camberwell"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Melbourne's Hard Water */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Local Expertise</p>
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
            We Understand Melbourne Hair
          </h2>
          <p className="text-lg text-[#5D4A3A] max-w-3xl mx-auto mb-8">
            Melbourne's hard water can cause blonde hair to become brassy and dull. Our specialists use professional-grade toners and recommend the right at-home care to keep your blonde bright and beautiful between appointments.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
              <h3 className="text-lg font-medium text-[#2C2420] mb-2">Anti-Brass Toners</h3>
              <p className="text-sm text-[#5D4A3A]">Professional-grade toners that neutralize unwanted warm tones</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
              <h3 className="text-lg font-medium text-[#2C2420] mb-2">Bond Protection</h3>
              <p className="text-sm text-[#5D4A3A]">K18 molecular repair treatments to maintain hair integrity during lightening</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
              <h3 className="text-lg font-medium text-[#2C2420] mb-2">Home Care Advice</h3>
              <p className="text-sm text-[#5D4A3A]">Expert recommendations for maintaining your blonde at home</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
            Ready for Your Perfect Colour?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a consultation at our Camberwell salon. Our colour specialists will create a custom colour plan tailored to your hair goals and lifestyle.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Blonde Consultation
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
